import type { FC } from "react";

import { format as formatDate } from "date-fns";
import type { TooltipProps } from "recharts";
import { isPresent } from "ts-extras";

import { ChartTooltip } from "src/components/charts/chart-tooltip";
import { GraphScale } from "./constants";
import type { DataPoint, GraphSeries } from "./types";
import { formatMetricValue, getTooltipSuffixTextFromMetric } from "./utils";

type Props = {
  graphSeries: GraphSeries[];
  hoveredLine?: string;
  scale: GraphScale;
  categoricalLabels?: string[];
} & TooltipProps<number, string>;

export const Tooltip: FC<Readonly<Props>> = ({
  payload,
  hoveredLine,
  graphSeries,
  scale,
  categoricalLabels,
}) => {
  if (!payload || !hoveredLine) return null;

  // The first payload will be the dataset that is being hovered over
  const calculatedAt = payload[0]?.payload.calculatedAt as
    | DataPoint["calculatedAt"]
    | null
    | undefined;

  if (calculatedAt === undefined || calculatedAt === null) return null;

  // Find the series that corresponds to the hovered line
  const series = graphSeries.find((series) => series.key === hoveredLine);

  // Find the data point in that series
  const point = series?.data.find(
    (point) => point.calculatedAt === calculatedAt,
  );

  if (!series || !point) return null;

  const displayedValue = isPresent(point.metricValue)
    ? formatMetricValue(point.metricValue, scale === GraphScale.Percent)
    : "Not available";

  const subtitles: string[] = [];

  // For categorical data (when categoricalLabels are provided), use the label
  // Otherwise format calculatedAt as a date for time-series data
  if (categoricalLabels && typeof point.calculatedAt === "number") {
    const label = categoricalLabels[point.calculatedAt];
    if (label) {
      subtitles.push(label);
    }
  } else {
    // Time-series data: format as date
    subtitles.push(formatDate(new Date(point.calculatedAt), "LLL d, yyyy"));
  }

  if (series.description && series.description !== series.metricName) {
    subtitles.unshift(series.description);
  }

  const description = point.description
    ? point.description
    : series.aggregation
      ? getTooltipSuffixTextFromMetric({
          noun: series.metricNoun,
          aggregation: series.aggregation,
          normalization: series.normalization,
        })
      : undefined;

  return (
    <ChartTooltip
      wrapperVariant="full"
      color={series.color}
      title={series.metricName}
      subtitles={subtitles}
      valueDescription={series.valueTooltipDescription}
      value={[
        {
          value: displayedValue,
          description,
        },
        ...(point.additionalTooltipValues ?? []),
      ]}
    />
  );
};
